// The three demo projects as Mosaico project cards, and the helpers that
// splice them into the Projects list.
//
// Each card id is a stable slug (never a real Mosaico UUID) so the route gate
// can recognise it in /projects/:id and render the ArcDisk page instead of
// asking the Rails API for a project that does not exist.
import type { ArcProject, DisplayStatus, ProjectTab } from 'pages/ArcProjectsList/types'
import { demoProjectList, projectFixtures } from './fixtures'
import { getProjectDemoState } from './demoState'

export interface DemoProjectRef {
  /** Mosaico-side id, used in /projects/:id */
  cardId: string
  /** ArcDisk project_id — key into the fixtures */
  adProjectId: string
}

export const DEMO_PROJECTS: DemoProjectRef[] = [
  {
    cardId: 'arcdisk-wonders-of-the-world-fair',
    adProjectId: 'proj_pleasant_view_ms_2026_27_5_wonders_of_the_world_fair',
  },
  {
    cardId: 'arcdisk-plant-investigation',
    adProjectId: 'proj_pleasant_view_ms_2026_27_6_plant_investigation_does_a_heat_lamp_help_plants_grow',
  },
  {
    cardId: 'arcdisk-the-factory',
    adProjectId: 'proj_pleasant_view_ms_2026_27_11_the_factory',
  },
]

// Where the demo cards land on page 1 of "My projects". 'end' appends them
// after the school's own projects (the agreed behaviour); flip to 'start' if
// the presenter's account has many projects and the cards end up below the
// fold.
export const DEMO_PROJECTS_PLACEMENT: 'start' | 'end' = 'end'

export const DEMO_SCHOOL_NAME = 'Pleasant View'

const byCardId = new Map(DEMO_PROJECTS.map(p => [p.cardId, p]))

export const findDemoProjectByCardId = (cardId: string | undefined): DemoProjectRef | undefined =>
  cardId ? byCardId.get(cardId) : undefined

export const isDemoProjectCardId = (cardId: string | undefined): boolean => byCardId.has(cardId ?? '')

export const demoProjectPath = (cardId: string) => `/projects/${cardId}`
export const demoStudentPath = (cardId: string, studentId: number | string) =>
  `/projects/${cardId}/students/${studentId}`

/** One sentence of facts for the card (no invented pedagogy copy). */
export function demoProjectSynopsis(ref: DemoProjectRef): string {
  const { meta } = projectFixtures[ref.adProjectId].dashboard
  const list = demoProjectList.find(p => p.project_id === ref.adProjectId)
  const parts = [
    meta.subjects.length ? meta.subjects.join(' · ') : null,
    `${meta.students_submitted} student${meta.students_submitted === 1 ? '' : 's'}`,
    list ? `${list.file_count} file${list.file_count === 1 ? '' : 's'} submitted` : null,
  ].filter(Boolean)
  return `${parts.join(' · ')}. Student work is assessed by ArcDisk AI against the Arc skills framework.`
}

export function demoProjectAsCard(ref: DemoProjectRef): ArcProject {
  const { meta, students } = projectFixtures[ref.adProjectId].dashboard
  const latest = students
    .map(s => s.latest_submission_at)
    .filter((d): d is string => !!d)
    .sort()
    .pop()
  return {
    id: ref.cardId,
    name: meta.title ?? ref.adProjectId,
    status: 'approved',
    synopsis: demoProjectSynopsis(ref),
    groupSchoolTermId: null,
    schoolTermId: null,
    groupName: DEMO_SCHOOL_NAME,
    kind: 'application',
    updatedAt: latest ?? new Date(0).toISOString(),
    schoolId: 'pleasant_view_ms',
    authorId: 'arcdisk-demo',
    projectGrades: meta.grade
      ? [{ id: `arcdisk-grade-${meta.grade}`, grade: { id: `arcdisk-grade-${meta.grade}`, name: `Grade ${meta.grade}` } }]
      : [],
    coTeachers: [],
    // The card's category chip doubles as the assessment status, so a card
    // never claims "AI assessed" before the presenter has run it.
    categories: [demoStatusCategory(ref.adProjectId)],
  }
}

function demoStatusCategory(adProjectId: string): ArcProject['categories'][number] {
  const { status } = getProjectDemoState(adProjectId)
  if (status === 'graded') return { id: 'arcdisk-status', name: 'AI assessed', color: '#D9D2E9' }
  if (status === 'grading') return { id: 'arcdisk-status', name: 'Assessing', color: '#FFF2CC' }
  return { id: 'arcdisk-status', name: 'Ready to assess', color: '#D9EAD3' }
}

export const demoProjectCards = (): ArcProject[] => DEMO_PROJECTS.map(demoProjectAsCard)

export interface ListContext {
  tab: ProjectTab
  page: number
  search: string
  status: DisplayStatus
}

/**
 * Splice the demo cards into the list the Rails API returned. Only on the
 * first page of "My projects", with no search text and a status filter that
 * an assigned project satisfies, so the cards never contradict the filters
 * the teacher applied.
 */
export function withDemoProjects(
  projects: ArcProject[],
  ctx: ListContext,
  placement: 'start' | 'end' = DEMO_PROJECTS_PLACEMENT,
): ArcProject[] {
  const show =
    ctx.tab === 'my' && ctx.page === 1 && ctx.search.trim() === '' && (ctx.status === 'all' || ctx.status === 'assigned')
  if (!show) return projects
  const cards = demoProjectCards()
  return placement === 'start' ? [...cards, ...projects] : [...projects, ...cards]
}
